/**
 * §4 수음 레벨 미터 — 마이크가 실제로 소리를 잡고 있는지 보여 준다.
 *
 * 이 화면 요소의 진짜 값어치는 **진단**이다: 입력이 서버가 알려 주는 VAD
 * 에너지 바닥(기본 −55, 정본은 서버 `VadConfig`) 아래면 아무리 말해도 발화가
 * 열리지 않는다. 그때 사용자에게 보이는 것은 "통역이 안 된다" 뿐이고 원인을
 * 알 길이 없다.
 *
 * ■ **레이아웃 불변식**(실사용 피드백 — "정신없어"):
 *   이 컴포넌트는 **절대 크기가 변하지 않는다**. 초기 구현은 저입력일 때
 *   미터가 사라지고 경고 배지가 그 자리를 차지했다 — 말의 자연스러운 강약만
 *   으로도 둘이 초당 여러 번 교대해 헤더 전체가 흔들렸다.
 *   그래서:
 *     · 미터는 **항상 같은 자리에 같은 크기로** 있다(숨기지 않는다).
 *     · 상태는 **미터 자체의 색조**로만 말한다(요소 교체 없음).
 *     · 힌트 텍스트는 **자리를 미리 잡아 둔 칸**에서 글자만 바뀐다
 *       (`min-width` + 고정 높이 — 나타나고 사라져도 폭이 변하지 않는다).
 *     · 깜빡임은 히스테리시스가 막는다(resampler.nextLowLevelState).
 *   애니메이션은 색 전환뿐이다 — 점멸 없음.
 */

import { useEffect, useRef, useState } from 'react'
import {
  deriveLevelThresholds,
  initialLowLevelState,
  levelZone,
  nextLowLevelState,
  SILENCE_DBFS,
  type LowLevelState,
} from '../lib/resampler'
import type { CaptureStats } from '../lib/capture'

/** dBFS → 바 채움 비율. -60dBFS 를 왼쪽 끝, 0dBFS 를 오른쪽 끝으로 본다. */
function fillRatio(dbfs: number): number {
  const min = -60
  if (dbfs <= min) return 0
  return Math.max(0, Math.min(1, (dbfs - min) / (0 - min)))
}

/** 표시 상태 — 색조 하나로 수렴시킨다(요소를 바꾸지 않는다). */
type Tone = 'idle' | 'ok' | 'warn' | 'hot'

const TONE_FILL: Record<Tone, string> = {
  idle: 'var(--color-muted-foreground)',
  ok: 'var(--color-success)',
  warn: 'var(--color-warning)',
  hot: 'var(--color-destructive)',
}

const TONE_HINT: Record<Tone, string> = {
  idle: '',
  ok: '',
  warn: '입력이 작습니다',
  hot: '입력이 큽니다',
}

const TONE_TITLE: Record<Tone, string> = {
  idle: '마이크가 꺼져 있거나 무음입니다',
  ok: '정상적으로 수음 중입니다',
  warn: '입력이 작습니다 — 마이크를 가까이 하거나 입력 볼륨을 올려 주세요',
  hot: '입력이 너무 커 왜곡될 수 있습니다',
}

/** peakDbfs 를 levelZone 의 peak 인자(선형 진폭)로 되돌린다. */
function dbfsToAmp(dbfs: number): number {
  return 10 ** (dbfs / 20)
}

export function LevelMeter({
  stats,
  active,
  floorDbfs,
}: {
  stats: CaptureStats | null
  /** 캡처 중이 아니면(중지·뮤트) 미터를 비활성 색조로 둔다 — 숨기지 않는다 */
  active: boolean
  /**
   * D2 — 서버가 caps 로 알린 VAD 에너지 바닥(dBFS). 미전달·null 이면
   * 기본값(−55)을 쓴다 — 구 서버·caps 미수신에서도 동작해야 한다.
   */
  floorDbfs?: number | null
}): React.ReactElement | null {
  const [low, setLow] = useState<LowLevelState>(initialLowLevelState)
  const lowRef = useRef(low)
  lowRef.current = low

  const th = deriveLevelThresholds(floorDbfs ?? undefined)
  const dbfs = active && stats ? stats.dbfs : SILENCE_DBFS

  // 저입력 경고는 **지속 시간**을 요구하므로 시간이 흘러야 판정이 끝난다.
  // 값 변화가 멈춰도(완전 무음) 타이머가 돌아야 하니 주기적으로 재평가한다.
  useEffect(() => {
    const tick = (): void => {
      setLow((prev) => {
        const next = nextLowLevelState(prev, dbfs, Date.now(), active, th)
        // 참조 동일성을 유지해 불필요한 리렌더를 막는다.
        return next.warn === prev.warn && next.since === prev.since ? prev : next
      })
    }
    tick()
    const timer = setInterval(tick, 400)
    return () => clearInterval(timer)
  }, [dbfs, active, th.enter, th.exit])

  if (!stats) return null

  const clipping =
    active && levelZone(dbfs, dbfsToAmp(stats.peakDbfs), th.enter) === 'hot'
  const tone: Tone = !active
    ? 'idle'
    : clipping
      ? 'hot'
      : low.warn
        ? 'warn'
        : levelZone(dbfs, undefined, th.enter) === 'silent'
          ? 'idle'
          : 'ok'

  return (
    <div className="flex items-center gap-2" data-testid="level-meter" data-tone={tone}>
      <div
        className="relative h-1.5 w-24 shrink-0 overflow-hidden rounded-full bg-[var(--color-muted)]"
        role="meter"
        aria-label="마이크 수음 레벨"
        aria-valuenow={Math.round(dbfs)}
        aria-valuemin={-60}
        aria-valuemax={0}
        title={TONE_TITLE[tone]}
      >
        <div
          className="h-full transition-[width,background-color] duration-150"
          style={{
            width: `${fillRatio(dbfs) * 100}%`,
            backgroundColor: TONE_FILL[tone],
          }}
        />
        {/* VAD 바닥 눈금 — 이 왼쪽에 머물면 발화가 열리지 않는다 */}
        <span
          aria-hidden
          className="absolute top-0 h-full w-px bg-[var(--color-border)]"
          style={{ left: `${fillRatio(th.enter) * 100}%` }}
        />
      </div>

      {/*
        힌트 칸 — **항상 존재한다**. 글자만 바뀌고 칸은 사라지지 않으므로
        경고가 켜지고 꺼져도 옆 요소가 밀리지 않는다(레이아웃 불변식).
        aria-live=polite: 스크린리더에는 바뀔 때만 읽힌다.
      */}
      <span
        className="min-w-[6.5rem] text-(length:--text-xs-size) leading-none transition-colors duration-150"
        style={{ color: tone === 'ok' || tone === 'idle' ? 'transparent' : TONE_FILL[tone] }}
        aria-live="polite"
        data-testid="level-hint"
      >
        {TONE_HINT[tone]}
      </span>
    </div>
  )
}
